import addAlert from 'ps-components/AlertToast/addAlert';
import apiClient from 'ps-components/lib/apiClient';
import cloneDeep from 'lodash/cloneDeep';

export const GET_REVIEW_IMPACT_START = 'GET_REVIEW_IMPACT_START';
export const GET_REVIEW_IMPACT_SUCCESS = 'GET_REVIEW_IMPACT_SUCCESS';
export const GET_REVIEW_IMPACT_ERROR = 'GET_REVIEW_IMPACT_ERROR';

export const GET_REVIEW_IMPACT_SELECTED_VIEW_ERROR = 'GET_REVIEW_IMPACT_SELECTED_VIEW_ERROR';
export const GET_REVIEW_IMPACT_SELECTED_VIEW_START = 'GET_REVIEW_IMPACT_SELECTED_VIEW_START';
export const GET_REVIEW_IMPACT_SELECTED_VIEW_SUCCESS = 'GET_REVIEW_IMPACT_SELECTED_VIEW_SUCCESS';

export const GET_REVIEW_IMPACT_VIEWS_ERROR = 'GET_REVIEW_IMPACT_VIEWS_ERROR';
export const GET_REVIEW_IMPACT_VIEWS_START = 'GET_REVIEW_IMPACT_VIEWS_START';
export const GET_REVIEW_IMPACT_VIEWS_SUCCESS = 'GET_REVIEW_IMPACT_VIEWS_SUCCESS';

export const REVIEW_IMPACT_LOAD_MORE_START = 'REVIEW_IMPACT_LOAD_MORE_START';
export const REVIEW_IMPACT_LOAD_MORE_SUCCESS = 'REVIEW_IMPACT_LOAD_MORE_SUCCESS';
export const REVIEW_IMPACT_LOAD_MORE_ERROR = 'REVIEW_IMPACT_LOAD_MORE_ERROR';

export const SET_REVIEW_IMPACT_SELECTED_VIEW_ERROR = 'SET_REVIEW_IMPACT_SELECTED_VIEW_ERROR';
export const SET_REVIEW_IMPACT_SELECTED_VIEW_START = 'SET_REVIEW_IMPACT_SELECTED_VIEW_START';
export const SET_REVIEW_IMPACT_SELECTED_VIEW_SUCCESS = 'SET_REVIEW_IMPACT_SELECTED_VIEW_SUCCESS';

export const TOGGLE_LOCATION = 'TOGGLE_LOCATION';

export function toggleLocation(location) {
  return { type: TOGGLE_LOCATION, location };
}

const FIRST_COLUMN_WIDTH = 115;

export function setLoading() {
  return { type: GET_REVIEW_IMPACT_START };
}

export function getReviewImpact(
  maid,
  {
    employeeFilters,
    employeeNameSearch,
    geoDifferentials,
    jobTitleSearch,
    locationLimit,
    locationOffset,
    locationSearch,
    locationType,
    orderBy
  }
) {
  return async dispatch => {
    const errorMessage = 'Error getting review impact data';

    try {
      dispatch({ type: GET_REVIEW_IMPACT_START });

      const locationAggregateCall = apiClient.apiPost(`/cms/api/accounts/${maid}/differential/geo/review`, {
        employeeFilters,
        employeeNameSearch,
        geoDifferentials,
        isLocationAggregate: true,
        jobTitleSearch,
        locationLimit,
        locationOffset,
        locationSearch,
        locationType
      });

      const employeeDataCall = apiClient.apiPost(`/cms/api/accounts/${maid}/differential/geo/review`, {
        employeeFilters,
        employeeNameSearch,
        geoDifferentials,
        isLocationAggregate: false,
        jobTitleSearch,
        locationLimit,
        locationOffset,
        locationSearch,
        locationType,
        orderBy: orderBy || undefined
      });

      const results = await Promise.all([locationAggregateCall, employeeDataCall]);
      let locationAggregateResult = results[0];
      const employeeDataResult = results[1];

      if (locationAggregateResult.status < 300 && employeeDataResult.status < 300) {
        // this total locations is used by pagination
        const totalPaginationLocations = employeeDataResult?.data?.data?.metadata?.totalLocationsCount || 0;
        // this total locations is used in the review-impact title (minus the All Locations rows)
        const totalNumberOfLocations = locationAggregateResult?.data?.data?.data?.length - 1 || 0;
        // this total employees is used in the review-impact title
        const employeeCountIndex = locationAggregateResult?.data?.data?.terms?.findIndex(
          t => t.term === 'employee_count_default'
        );
        const totalNumberOfEmployees = locationAggregateResult?.data?.data?.data?.[0]?.[employeeCountIndex];

        locationAggregateResult = modifyLocationData(
          locationAggregateResult,
          employeeDataResult,
          locationType,
          geoDifferentials
        );

        // make sure the first column in both results has 115 width (to make room for icons)
        if (employeeDataResult.data.data) {
          employeeDataResult.data.data.terms[0].metadata.width = FIRST_COLUMN_WIDTH;
        }
        if (locationAggregateResult.data.data) {
          locationAggregateResult.data.data.terms[0].metadata.width = FIRST_COLUMN_WIDTH;
        }

        dispatch({
          type: GET_REVIEW_IMPACT_SUCCESS,
          employeeData: employeeDataResult?.data?.data,
          locationData: locationAggregateResult?.data?.data,
          totalNumberOfEmployees,
          totalNumberOfLocations,
          totalPaginationLocations,
          locationType
        });
      } else {
        throw Error(errorMessage);
      }

      return true;
    } catch (error) {
      addAlert(errorMessage, 'danger');
      console.error('Catch Error: Getting Review Impact Data: ', error);
      dispatch({ type: GET_REVIEW_IMPACT_ERROR });

      return false;
    }
  };
}

export function reviewImpactLoadMore(
  maid,
  {
    employeeFilters,
    employeeLimit,
    employeeNameSearch,
    employeeOffset,
    geoDifferentials,
    jobTitleSearch,
    location,
    locationType,
    orderBy
  }
) {
  return async dispatch => {
    const errorMessage = 'Error getting more employee data';

    try {
      dispatch({ type: REVIEW_IMPACT_LOAD_MORE_START, location });

      const employeeDataResult = await apiClient.apiPost(`/cms/api/accounts/${maid}/differential/geo/review`, {
        employeeFilters,
        employeeNameSearch,
        employeeLimit,
        employeeOffset,
        geoDifferentials,
        isLocationAggregate: false,
        jobTitleSearch,
        location,
        locationType,
        orderBy: orderBy || undefined
      });

      if (employeeDataResult.status < 300) {
        // make sure the first column has 115 width (to make room for icons)
        if (employeeDataResult.data.data) {
          employeeDataResult.data.data.terms[0].metadata.width = FIRST_COLUMN_WIDTH;
        }

        dispatch({
          type: REVIEW_IMPACT_LOAD_MORE_SUCCESS,
          employeeData: employeeDataResult?.data?.data,
          location,
          showMoreLimit: location === 'Not Set' ? 20 : 50
        });
      } else {
        throw Error(errorMessage);
      }

      return true;
    } catch (error) {
      addAlert(errorMessage, 'danger');
      console.error('Catch Error getting more employee data: ', error);
      dispatch({ type: REVIEW_IMPACT_LOAD_MORE_ERROR, location });

      return false;
    }
  };
}

// We want the locationAggregate result to have the same terms as employeeDataResult
// so the main rows and the sub rows will match data
// Also, the first column for locationAggregate needs to be the default location column
function modifyLocationData(locationAggregateResult, employeeDataResult, locationType, geoDifferentials) {
  if (locationAggregateResult?.data?.data && employeeDataResult?.data?.data) {
    const locationTerms = locationAggregateResult.data.data.terms;
    const locationData = locationAggregateResult.data.data.data;
    const employeeData = employeeDataResult.data.data.data;
    const employeeTerms = employeeDataResult.data.data.terms;
    const employeeTermGroups = employeeDataResult.data.data.termGroups;

    const newLocationAggregateResult = cloneDeep(locationAggregateResult);
    newLocationAggregateResult.data.data.terms = cloneDeep(employeeTerms);
    newLocationAggregateResult.data.data.termGroups = cloneDeep(employeeTermGroups);
    const newData = [];

    const employeeIndex = locationTerms?.findIndex(t => t.term === 'employee_count_default');
    let locationIndex, cityIndex, stateIndex, cityOrState;
    let employeeDataLocationIndex;
    if (locationType === 'work-location') {
      locationIndex = locationTerms?.findIndex(t => t.term === 'work_location_default');
      employeeDataLocationIndex = employeeTerms?.findIndex(t => t.term === 'work_location_default');
    } else {
      locationIndex = locationTerms?.findIndex(t => t.term === 'city_state_default');
      cityIndex = locationTerms?.findIndex(t => t.term === 'city_default');
      stateIndex = locationTerms?.findIndex(t => t.term === 'state_default');
      employeeDataLocationIndex = employeeTerms?.findIndex(t => t.term === 'city_state_default');
    }

    const allValidLocations = employeeData.map(e => e[employeeDataLocationIndex]);
    allValidLocations.push('All Locations');

    for (let i = 0; i < locationData?.length; i++) {
      const thisLocationRow = locationData[i];
      const location = thisLocationRow[locationIndex];

      if (locationType === 'city-state') {
        const city = thisLocationRow[cityIndex];
        const state = thisLocationRow[stateIndex];
        if (!city || !state) {
          cityOrState = city || state;
        }
      }

      // only include location rows that have data in employeeData (changes based on filter or pagination)
      if (allValidLocations.includes(location)) {
        const newDataRow = [];

        for (let j = 0; j < employeeTerms.length; j++) {
          const thisEmployeeTerm = employeeTerms[j];

          if (j === 0) {
            const numberOfEmployees = Number(thisLocationRow[employeeIndex]);
            const appliedGeoForLocation =
              `${(
                geoDifferentials.find(g => g.location === location || g.location === cityOrState)?.differential * 100
              )?.toFixed(2)}%` || 0;
            if (location === 'All Locations') {
              newDataRow.push(`${location} (ees: ${numberOfEmployees})`);
            } else {
              newDataRow.push(`${cityOrState || location} (ees: ${numberOfEmployees}, diff: ${appliedGeoForLocation})`);
            }
          } else {
            const thisGeoDiffName = thisEmployeeTerm.metadata.geoDiffName;

            if (thisGeoDiffName != null) {
              // check if locationAggregate terms has this employee geo-diff column; if so, use its location aggregate value
              // else, insert a null value so the main and sub row columns will match
              const locationIndex = locationTerms.findIndex(
                t => t.term === thisEmployeeTerm.term && t.metadata.geoDiffName === thisGeoDiffName
              );

              if (locationIndex != null) {
                newDataRow.push(thisLocationRow[locationIndex]);
              } else {
                newDataRow.push(null);
              }
            } else if (thisEmployeeTerm.term?.includes('_default')) {
              // also include _default data
              const defaultIndex = locationTerms.findIndex(t => t.term === thisEmployeeTerm.term);

              if (defaultIndex != null) {
                newDataRow.push(thisLocationRow[defaultIndex]);
              } else {
                newDataRow.push(null);
              }
            } else {
              newDataRow.push(null);
            }
          }
        }

        newData.push(newDataRow);
        cityOrState = null;
      }
    }

    newLocationAggregateResult.data.data.data = newData;

    return newLocationAggregateResult;
  }

  return locationAggregateResult;
}

export function getReviewImpactViews() {
  return async dispatch => {
    const errorMessage = 'Error retrieving review impact views';

    try {
      dispatch({ type: GET_REVIEW_IMPACT_VIEWS_START });
      const masterAccountId = window.app.masterAccountId;
      const result = await apiClient.apiGet(`/cms/api/accounts/${masterAccountId}/views`, {
        params: {
          contextName: 'geo_differential',
          portAll: false,
          isFacet: false
        }
      });

      if (result.status < 300) {
        dispatch({
          type: GET_REVIEW_IMPACT_VIEWS_SUCCESS,
          views: result.data?.data?.views,
          defaultViewId: result?.data?.data?.defaultViewId
        });
      } else {
        throw Error(errorMessage);
      }
    } catch (error) {
      addAlert(errorMessage, 'danger');
      console.log('Catch Error: Get Review Impact Views: ', error);
      dispatch({ type: GET_REVIEW_IMPACT_VIEWS_ERROR });
    }
  };
}

export function getReviewImpactSelectedView() {
  return async dispatch => {
    const errorMessage = 'Error retrieving selected review impact view';

    try {
      dispatch({ type: GET_REVIEW_IMPACT_SELECTED_VIEW_START });
      const masterAccountId = window.app.masterAccountId;
      const result = await apiClient.apiGet(`/cms/api/accounts/${masterAccountId}/views/last-viewed`, {
        params: {
          contextName: 'geo_differential'
        }
      });

      if (result.status < 300) {
        dispatch({ type: GET_REVIEW_IMPACT_SELECTED_VIEW_SUCCESS, selectedViewPk: result.data?.data?.view_pk });
        return result.data?.data?.view;
      } else {
        throw Error(errorMessage);
      }
    } catch (error) {
      addAlert(errorMessage, 'danger');
      console.log('Catch Error: Get Review Impact Selected View: ', error);
      dispatch({ type: GET_REVIEW_IMPACT_SELECTED_VIEW_ERROR });
    }
  };
}

export function setReviewImpactSelectedView(viewPk) {
  return async dispatch => {
    const errorMessage = 'Error setting selected review impact view';

    try {
      dispatch({ type: SET_REVIEW_IMPACT_SELECTED_VIEW_START });

      const masterAccountId = window.app.masterAccountId;
      const result = await apiClient.apiPost(`/cms/api/accounts/${masterAccountId}/views/last-viewed`, {
        contextName: 'geo_differential',
        viewId: viewPk
      });

      if (result.status < 300) {
        dispatch({ type: SET_REVIEW_IMPACT_SELECTED_VIEW_SUCCESS, selectedViewPk: viewPk });
      } else {
        throw Error(errorMessage);
      }
    } catch (error) {
      addAlert(errorMessage, 'danger');
      console.log('Catch Error, Set Review Impact Selected View: ', error);
      dispatch({ type: SET_REVIEW_IMPACT_SELECTED_VIEW_ERROR });
    }
  };
}
